(function() {
  'use strict';

  // Shared utility functions

  /**
   * Fetches collection HTML from the given URL with the specified section ID
   * @param {string} url - The collection URL to fetch
   * @param {string} sectionId - The section ID to append as a query parameter
   * @returns {Promise<string>} The HTML content, or null if fetch fails
   */
  async function fetchCollectionHTML(url, sectionId) {
    const absUrl = new URL(url, window.location.origin);
    absUrl.searchParams.set('section_id', sectionId);

    try {
      const res = await fetch(absUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return await res.text();
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  /**
   * Parses HTML string and extracts the .row element
   * @param {string} html - The HTML string to parse
   * @returns {HTMLElement|null} The .row element, or null if not found
   */
  function parseCollectionRow(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.querySelector('.row');
  }

  /**
   * Scrolls smoothly to the given row element, accounting for header height
   * @param {HTMLElement} row - The row element to scroll to
   */
  function scrollToRow(row) {
    const px = (v) => {
      const n = parseFloat(String(v).trim());
      return Number.isNaN(n) ? 0 : n;
    };
    const headerH = () => px(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;

    requestAnimationFrame(() => {
      const y = row.getBoundingClientRect().top + window.pageYOffset - headerH();
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    });
  }

  /**
   * Tracks page events for analytics (supports both gtag.js GA4 and Google Tag Manager)
   * @param {string} pageTitle - The page title for the event
   * @param {string} pagePath - The page path for the event
   */
  function trackPageEvent(pageTitle, pagePath) {
    // Track using gtag.js (GA4) if available
    if (typeof gtag !== 'undefined' && typeof gtag === 'function') {
      try {
        gtag('event', 'page_view', {
          page_title: pageTitle,
          page_path: pagePath
        });
      } catch (e) {
        console.error('Error tracking with gtag:', e);
      }
    }

    // Track using Google Tag Manager (GTM) if available
    if (typeof dataLayer !== 'undefined' && Array.isArray(dataLayer)) {
      try {
        dataLayer.push({
          event: 'virtual_pageview',
          page_title: pageTitle,
          page_path: pagePath
        });
      } catch (e) {
        console.error('Error tracking with dataLayer:', e);
      }
    }
  }

  // Cache for storing fetched display-all content
  const displayAllCache = {
    row: null,
    url: null
  };

  // Handler for js-collection-display-all
  document.addEventListener('click', async (evt) => {
    const viewMode = 'new-page';

    const link = evt.target.closest('.js-collection-display-all');
    if (!link) return;
    evt.preventDefault();

    const collectionUrl = link.href;
    if (!collectionUrl) return;

    // Store the current page URL for the Return link
    const currentPageUrl = window.location.href;

    // Append ?view=view-all to ensure we load the collection with the expected template
    const url = new URL(collectionUrl, window.location.origin);



    if (viewMode === 'new-page') {
      //the 'all' template mirrors the default template so we get the same view
      url.searchParams.set('view', 'all');

      //preload the page so we have it cached
      const html = await fetchCollectionHTML(url.toString(), 'collection-view-all');

      //then remember the scroll position
      sessionStorage.setItem('collectionScrollY', String(window.scrollY));

      //switch pages!
      window.location.href = url.toString();
      return;
    }else{
      url.searchParams.set('view', 'view-all');
      const urlWithView = url.toString();
      // Find the .collection-products container
      const collectionProducts = document.querySelector('.collection-products');
      if (!collectionProducts) {
        window.location.href = urlWithView;
        return;
      }

      let displayAllRow = displayAllCache.row;

      // Check if we already have cached content for this URL
      if (!displayAllRow || displayAllCache.url !== urlWithView) {
        const html = await fetchCollectionHTML(urlWithView, 'collection-view-all');
        if (!html) {
          window.location.href = urlWithView;
          return;
        }

        displayAllRow = parseCollectionRow(html);
        if (!displayAllRow) {
          window.location.href = urlWithView;
          return;
        }

        displayAllRow.classList.add('collection-products--view-all');
        
        // Cache the row and URL
        displayAllCache.row = displayAllRow;
        displayAllCache.url = urlWithView;
      }

      // Hide all original row divs instead of removing them
      const originalRows = collectionProducts.querySelectorAll('.row:not(.collection-products--view-all)');
      originalRows.forEach(row => {
        row.classList.add('js-display-none');
      });

      // Show the display-all row - append if not in the actual DOM container
      const isInContainer = collectionProducts.contains(displayAllRow);
      if (!isInContainer) {
        collectionProducts.appendChild(displayAllRow);
      }
      
      // Always ensure the display-all row is visible
      displayAllRow.classList.remove('js-display-none');

      // Toggle link visibility: hide Display All, show Return
      const displayAllLink = document.querySelector('.js-collection-display-all');
      const returnLink = document.querySelector('.js-collection-return');
      
      if (displayAllLink) {
        displayAllLink.classList.add('js-display-none');
        displayAllLink.setAttribute('hidden', '');
        displayAllLink.setAttribute('aria-hidden', 'true');
        displayAllLink.setAttribute('tabindex', '-1');
      }
      
      if (returnLink) {
        returnLink.classList.remove('js-display-none');
        returnLink.removeAttribute('hidden');
        returnLink.setAttribute('aria-hidden', 'false');
        returnLink.removeAttribute('tabindex');
        returnLink.href = currentPageUrl;
      }

      // Add display-all-active class to body
      document.body.classList.add('display-all-active');

      // Scroll to the new row
      // scrollToRow(displayAllRow);

      // Track the Display All event
      const urlObj = new URL(urlWithView);
      trackPageEvent('Display All Collection', urlObj.pathname + urlObj.search);

      document.dispatchEvent(new CustomEvent('collectionDisplayAll:mounted'));
    }
  });

  // Handler for js-collection-return
  document.addEventListener('click', async (evt) => {
    const link = evt.target.closest('.js-collection-return');
    if (!link) return;
    evt.preventDefault();

    // Find the .collection-products container
    const collectionProducts = document.querySelector('.collection-products');
    if (!collectionProducts) return;

    // Show all original row divs
    const originalRows = collectionProducts.querySelectorAll('.row:not(.collection-products--view-all)');
    originalRows.forEach(row => {
      row.classList.remove('js-display-none');
    });

    // Hide the display-all row
    const displayAllRow = collectionProducts.querySelector('.row.collection-products--view-all');
    if (displayAllRow) {
      displayAllRow.classList.add('js-display-none');
    }

    // Toggle link visibility: show Display All, hide Return
    const displayAllLink = document.querySelector('.js-collection-display-all');
    const returnLink = document.querySelector('.js-collection-return');
    
    if (displayAllLink) {
      displayAllLink.classList.remove('js-display-none');
      displayAllLink.removeAttribute('hidden');
      displayAllLink.setAttribute('aria-hidden', 'false');
      displayAllLink.removeAttribute('tabindex');
    }
    
    if (returnLink) {
      returnLink.classList.add('js-display-none');
      returnLink.setAttribute('hidden', '');
      returnLink.setAttribute('aria-hidden', 'true');
      returnLink.setAttribute('tabindex', '-1');
    }

    // Remove display-all-active class from body
    document.body.classList.remove('display-all-active');

    document.dispatchEvent(new CustomEvent('collectionReturn:mounted'));
  });

  // Handler for js-view-the-whole-collection
  document.addEventListener('click', async (evt) => {
    const link = evt.target.closest('.js-view-the-whole-collection');
    if (!link) return;
    evt.preventDefault();

    const currentRow = link.closest('.row');
    if (!currentRow) {
      window.location.href = link.dataset.collection;
      return;
    }

    const collectionUrl = link.dataset.collection;
    if (!collectionUrl) {
      window.location.href = link.dataset.collection;
      return;
    }

    const html = await fetchCollectionHTML(collectionUrl, 'collection-view-all');
    if (!html) {
      window.location.href = collectionUrl;
      return;
    }

    const newRow = parseCollectionRow(html);
    if (!newRow) {
      window.location.href = collectionUrl;
      return;
    }

    newRow.classList.add('collection-products--view-all');

    currentRow.replaceWith(newRow);

    // Hide all other .row elements in .collection-products, keeping only the new row visible
    const collectionProducts = newRow.closest('.collection-products');
    if (collectionProducts) {
      const allRows = collectionProducts.querySelectorAll('.row');
      allRows.forEach(row => {
        if (row !== newRow) {
          row.style.display = 'none';
        }
      });
    }

    scrollToRow(newRow);

    // Track the View Whole Collection event
    const urlObj = new URL(collectionUrl, window.location.origin);
    urlObj.searchParams.set('section_id', 'collection-view-all');
    trackPageEvent('View Whole Collection', urlObj.pathname + urlObj.search);

    document.dispatchEvent(new CustomEvent('collectionViewAll:mounted'));
  });

})();